// src/modules/requerimiento/components/FinalizarTrabajoCampoButton.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from "@mui/material";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getAdicionAsignados, updateRequerimiento } from "../services/reqSolicitudes";

export default function FinalizarTrabajoCampoButton({
  cod,
  onDone,           // opcional: callback al finalizar (refrescar pantalla padre)
  sticky = true,    // si quieres que quede pegado al bottom
  sx,               // estilos extra
}) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [errFinalizar, setErrFinalizar] = useState(null);

  const fetch = useCallback(async () => {
    if (!cod) return;
    try {
      setLoading(true);
      setErr(null);
      const { data } = await getAdicionAsignados(cod);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || "No se pudo cargar asignados");
    } finally {
      setLoading(false);
    }
  }, [cod]);

  useEffect(() => { fetch(); }, [fetch]);

  const isAsignado = useCallback((s) => String(s ?? "").trim().toLowerCase() === "asignado", []);
  const pendientes = useMemo(() => items.filter(r => isAsignado(r?.estado)), [items, isAsignado]);

  const puedeFinalizar = !loading && !err && items.length > 0 && pendientes.length === 0;

  const handleFinalizar = async () => {
    try {
      setFinishing(true);
      setErrFinalizar(null);
      await updateRequerimiento(cod, { estado: 7 });
      setConfirmar(false);
      if (typeof onDone === "function") await onDone();
    } catch (e) {
      setErrFinalizar(e?.response?.data?.message || e?.message || "No se pudo finalizar");
    } finally {
      setFinishing(false);
      fetch();
    }
  };

  const ayuda = loading
    ? "Cargando…"
    : err
    ? String(err)
    : puedeFinalizar
    ? "Todo listo: no quedan pendientes."
    : items.length === 0
    ? "Todavía no hay equipos en este requerimiento."
    : `Aún hay ${pendientes.length} pendiente(s).`;

  const wrapperSx = sticky
    ? {
        position: "sticky",
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1.5,
        py: 1.5,
        px: 1.5,
        bgcolor: "background.paper",
        borderTop: 1,
        borderColor: "divider",
        ...sx,
      }
    : { display: "flex", alignItems: "center", gap: 1.5, ...sx };

  return (
    <Box sx={wrapperSx}>
      <Typography variant="body2" color={err ? "error" : "text.secondary"} sx={{ flex: 1, textAlign: "right" }}>
        {ayuda}
      </Typography>
      <Button
        variant="contained"
        color="success"
        onClick={() => setConfirmar(true)}
        disabled={!puedeFinalizar || finishing}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : puedeFinalizar ? <CheckCircleIcon /> : <HourglassEmptyIcon />}
        sx={{ textTransform: "none", fontWeight: 700, flexShrink: 0 }}
      >
        Finalizar trabajo de campo
      </Button>

      <Dialog
        open={confirmar}
        onClose={() => !finishing && setConfirmar(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Finalizar trabajo de campo</DialogTitle>
        <DialogContent>
          <Typography variant="body2">El requerimiento pasará a <b>Revisión en Sistemas</b>.</Typography>
          {errFinalizar && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{errFinalizar}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(false)} disabled={finishing} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button
            onClick={handleFinalizar}
            variant="contained"
            color="success"
            disabled={finishing}
            startIcon={finishing ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {finishing ? "Finalizando…" : "Finalizar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
